import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

type ScrollAreaProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Scrollable container with hidden scrollbar and inset shadows
 * at top/bottom edges to indicate more content.
 */
export default function HostedScrollArea({
  children,
  className = '',
}: ScrollAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    update();

    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [update]);

  return (
    <div className="relative flex-1 min-h-0">
      {canScrollUp && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-3 shadow-[inset_0_8px_6px_-6px_rgba(0,0,0,0.08)]" />
      )}
      <div
        ref={ref}
        className={`h-full overflow-y-auto scrollbar-hidden ${className}`}
      >
        {children}
      </div>
      {canScrollDown && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-3 shadow-[inset_0_-8px_6px_-6px_rgba(0,0,0,0.08)]" />
      )}
    </div>
  );
}
